import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import type { FileNode, Group, Principal } from "../../api.ts";
import { formatCount } from "../../lib/format.ts";
import {
  audienceQuery,
  groupsQuery,
  meQuery,
  messageOf,
  useSetAudience,
} from "../../lib/queries.ts";
import { ConfirmDialog } from "../ConfirmDialog.tsx";
import { CopyButton } from "../CopyButton.tsx";
import { Icon, type IconName } from "../Icon.tsx";
import { EmailAddForm } from "../people/EmailAddForm.tsx";
import { PersonRow } from "../people/PersonRow.tsx";
import ui from "../ui.module.css";
import { AudienceSummary } from "./AudienceSummary.tsx";
import styles from "./FilePage.module.css";

export function AccessPanel({ node, embedded = false }: { node: FileNode; embedded?: boolean }) {
  const { data: audience } = useSuspenseQuery(audienceQuery(node.id));
  const me = useQuery(meQuery);
  const groups = useQuery(groupsQuery);
  const setAudience = useSetAudience(node.id);
  const inputId = useId();
  const [publishing, setPublishing] = useState(false);

  const owner = me.data?.email.toLowerCase();
  const principals = audience.principals;
  const emails = principals.flatMap((p) => (p.kind === "email" ? [p.email] : []));
  const groupIds = principals.flatMap((p) => (p.kind === "group" ? [p.groupId] : []));
  const allGroups = groups.data?.items ?? [];
  const shared = groupIds.map(
    (id): Group => allGroups.find((g) => g.id === id) ?? unknownGroup(id),
  );
  const offered = allGroups.filter((g) => !groupIds.includes(g.id));
  const reached = audience.effective.emails.length;
  const isPublic = principals.some((p) => p.kind === "public");
  const thing = node.kind === "folder" ? "folder" : "file";

  const save = (next: Principal[]) => setAudience.mutate(next);
  const setPublic = (on: boolean) => {
    const rest = principals.filter((p) => p.kind !== "public");
    save(on ? [...rest, { kind: "public" }] : rest);
  };
  const addGroup = (group: Group) => save([...principals, { kind: "group", groupId: group.id }]);
  const matchGroup = (text: string) => {
    const group = offered.find((g) => g.name.toLowerCase() === text.toLowerCase());
    if (group) addGroup(group);
    return group !== undefined;
  };

  const failure = setAudience.error ? `Access did not save: ${messageOf(setAudience.error)}` : null;
  const summary = audience.effective.public
    ? "Anyone with the link"
    : reached === 0
      ? "Only you"
      : `You and ${formatCount(reached, "other person", "others")}`;

  return (
    <section
      className={embedded ? styles.panel : `${ui.card} ${styles.panel}`}
      aria-labelledby={embedded ? undefined : `${inputId}-heading`}
      aria-label={embedded ? `Sharing ${node.name}` : undefined}
    >
      {embedded ? null : (
        <div className={styles.panelHead}>
          <h2 id={`${inputId}-heading`} className={styles.panelTitle}>
            Share
          </h2>
        </div>
      )}

      <div className={styles.linkBlock}>
        <p className={styles.fieldLabel}>Link</p>
        <div className={styles.linkField}>
          <code className={styles.link} data-testid="share-link">
            {node.url}
          </code>
          <CopyButton value={node.url} />
        </div>
      </div>

      <AudienceSummary node={node} audience={audience} />

      <div className={styles.access}>
        <p id={`${inputId}-access`} className={styles.fieldLabel}>
          {node.kind === "folder" ? "Who can open what is inside" : "Who can open the link"}
        </p>
        <div
          role="radiogroup"
          aria-labelledby={`${inputId}-access`}
          className={styles.accessOptions}
        >
          <AccessOption
            name={`${inputId}-access`}
            icon="lock"
            label="Only people added"
            detail="Sign-in required"
            checked={!isPublic}
            onPick={() => setPublic(false)}
          />
          <AccessOption
            name={`${inputId}-access`}
            icon="globe"
            danger
            label="Anyone with the link"
            detail="No sign-in"
            checked={isPublic}
            onPick={() => setPublishing(true)}
          />
        </div>
      </div>

      <ConfirmDialog
        open={publishing}
        title={`Let anyone with the link open ${node.name}?`}
        confirmLabel="Let anyone open it"
        cancelLabel="Keep sign-in required"
        onCancel={() => setPublishing(false)}
        onConfirm={() => {
          setPublishing(false);
          setPublic(true);
        }}
      >
        <p>
          Anyone holding the link opens the {thing} without signing in, so forwarding the link
          forwards the {thing}.
        </p>
      </ConfirmDialog>

      <div className={styles.people}>
        <div className={styles.peopleHead}>
          <label className={styles.fieldLabel} htmlFor={inputId}>
            People with access
          </label>
          <span className={styles.peopleCount} data-testid="viewer-summary">
            {summary}
          </span>
        </div>

        <EmailAddForm
          inputId={inputId}
          existing={emails}
          owner={owner}
          placeholder={allGroups.length > 0 ? "alice@example.com or a group" : "alice@example.com"}
          failure={failure}
          onAdd={(fresh) =>
            save([...principals, ...fresh.map((email) => ({ kind: "email" as const, email }))])
          }
          onText={matchGroup}
        />

        {offered.length > 0 ? (
          <fieldset className={styles.groupPicks}>
            <legend className={styles.groupPicksLabel}>Groups</legend>
            {offered.map((group) => (
              <button
                key={group.id}
                type="button"
                className={`${ui.button} ${styles.groupPick}`}
                onClick={() => addGroup(group)}
                aria-label={`Add group ${group.name}, ${formatCount(group.members.length, "person", "people")}`}
              >
                <Icon name="people" />
                <span className={styles.groupPickName}>{group.name}</span>
                <span className={styles.groupPickCount}>{group.members.length}</span>
              </button>
            ))}
          </fieldset>
        ) : null}

        <ul className={styles.personList}>
          {owner ? <PersonRow seed={owner} owner title={owner} detail="You · Owner" /> : null}
          {shared.map((group) => (
            <PersonRow
              key={group.id}
              seed={group.name}
              group
              title={
                <Link to="/groups/$id" params={{ id: group.id }} className={styles.personLink}>
                  {group.name}
                </Link>
              }
              detail={`Group · ${formatCount(group.members.length, "person", "people")}`}
              removeLabel={`Remove group ${group.name}`}
              onRemove={() =>
                save(principals.filter((p) => !(p.kind === "group" && p.groupId === group.id)))
              }
            />
          ))}
          {emails.map((email) => (
            <PersonRow
              key={email}
              seed={email}
              title={email}
              detail="Can view"
              removeLabel={`Remove ${email}`}
              onRemove={() =>
                save(principals.filter((p) => !(p.kind === "email" && p.email === email)))
              }
            />
          ))}
        </ul>
        {reached === 0 && !audience.effective.public ? (
          <p className={styles.peopleEmpty}>
            <span className={styles.emptyMark}>
              <Icon name="lock" />
            </span>
            Nobody else can open this {thing}.
          </p>
        ) : null}
      </div>
    </section>
  );
}

type AccessOptionProps = {
  name: string;
  icon: IconName;
  label: string;
  detail: string;
  checked: boolean;
  danger?: boolean;
  onPick: () => void;
};

export function AccessOption({
  name,
  icon,
  label,
  detail,
  checked,
  danger = false,
  onPick,
}: AccessOptionProps) {
  return (
    <label
      className={styles.accessOption}
      data-checked={checked || undefined}
      data-public={danger || undefined}
    >
      <input
        type="radio"
        name={name}
        className={styles.accessInput}
        checked={checked}
        onChange={onPick}
      />
      <span className={styles.accessIcon} aria-hidden="true">
        <Icon name={icon} className={styles.accessGlyph} />
      </span>
      <span className={styles.accessText}>
        <span className={styles.accessName}>{label}</span>
        <span className={styles.accessDetail}>{detail}</span>
      </span>
    </label>
  );
}

const unknownGroup = (id: string): Group => ({
  id,
  name: "Group",
  members: [],
  usedBy: [],
  createdAt: new Date(0).toISOString(),
});
